import type { Timestamp } from "firebase/firestore";
import type { OrgFeature } from "@/types/features";
import type { OrgMaintenance } from "@/types/maintenance";
import type {
  OrgIntegrationConfig,
  GoogleIntegration,
  MicrosoftIntegration,
} from "@/types/integrations";

// ─── Orgs ────────────────────────────────────────────────────────────────────

export interface Org {
  name: string;
  slug: string;
  logo: string | null;
  brandColor?: string | null;
  status: "active" | "suspended";
  ownerId: string | null; // uid of any orgUsers doc (platform or org-level)
  features?: Record<string, OrgFeature>;
  maintenance?: OrgMaintenance | null;
  integrations?: OrgIntegrationConfig;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ─── Studios ─────────────────────────────────────────────────────────────────

export interface Studio {
  orgId: string;
  name: string;
  pickaxeStudioId: string | null; // UUID extracted from workspaceUrl (optional)
  pickaxeApiKey: string; // Encrypted — never store raw
  workspaceUrl: string | null; // Full Pickaxe workspace URL (e.g. https://pickaxe.co/user/workspace/UUID/settings)
  loginUrl: string | null; // Custom domain URL for invite emails (2nd link in Pickaxe Domain settings), falls back to studioUrl
  studioUrl: string | null; // Base domain URL (1st link in Pickaxe Domain settings, e.g. https://studio.pickaxe.co/STUDIOABC)
  status: "active" | "suspended";
  createdAt: Timestamp;
  createdBy: string;
  lastSyncAt: Timestamp | null;
  agentSyncedAt: Timestamp | null;
  agentCount: number | null;
  historyVisibleToOrg: boolean;
  documentsVisibleToOrg: boolean;
  mcpSharedSecret: string | null; // Encrypted MCP shared secret for workspace integrations
  /**
   * Which Pickaxe API contract this studio is on.
   * - "legacy" (or absent): pre-April-2026 shape — products[], POST /pickaxe/info, etc.
   * - "2026-04": current contract — accessGroupId, GET /pickaxe/<id>, Actions/MCP/Deployment namespaces.
   * Absence is treated as "legacy" throughout the code.
   */
  pickaxeApiVersion?: "legacy" | "2026-04" | null;
  /**
   * Capabilities returned by GET /studio/whoami. Indicates which Pickaxe API
   * namespaces the stored API key is allowed to call. Absence (or any individual
   * field undefined) is treated optimistically as "available" so we don't
   * accidentally hide working features for studios that haven't been refreshed.
   */
  pickaxeCapabilities?: {
    pickaxes?: boolean;
    deployments?: boolean;
    actions?: boolean;
    mcps?: boolean;
    accessGroups?: boolean;
    products?: boolean;
    aiBuild?: boolean;
  } | null;
  /** When the whoami response was last persisted. Use to detect staleness. */
  pickaxeWhoamiAt?: Timestamp | null;
  /** Workspace name as reported by Pickaxe at the last whoami call. */
  pickaxeWorkspaceName?: string | null;
  /** Encrypted webhook secret for Pickaxe event webhooks (signup, payment, etc.) */
  webhookSecret?: string | null;
  tosResourceId: string | null;
  tosResourceName: string | null;
  tosLabel: string | null;
}

// ─── Agents (subcollection: studios/{studioId}/agents/{pickaxeId}) ──────────

export interface Agent {
  pickaxeId: string;
  name: string;
  description: string;
  type: "chat" | "form";
  listed: boolean;
  model: string | null;
  privacy: string | null;
  lastSyncedAt: Timestamp;
}

// ─── Status Monitoring (subcollection: studios/{studioId}/statusComponents) ─

export type StatusComponentType =
  | "primaryUrl"
  | "secondaryUrl"
  | "pickaxePlatform"
  | "llmProvider"
  | "agent";

export type StatusState = "up" | "down" | "degraded" | "unknown";

/** Widened from a union so the catalog can add providers without type churn. */
export type LlmProvider = string;

export interface StatusComponent {
  type: StatusComponentType;
  label: string;
  target: string | null; // URL for http checks, pickaxeId for agents, provider id for llmProvider
  provider: LlmProvider | null; // llmProvider components only
  assignedLlmProvider: LlmProvider | null; // agent components only
  assignedModel: string | null; // agent components only
  /** True when we actively poll a live feed for this component. False = gray + link-only. */
  monitored: boolean;
  /** Click-through URL: the component's primary URL, Pickaxe status, or provider status page. */
  statusPageUrl: string | null;
  currentStatus: StatusState;
  consecutiveFailures: number;
  lastCheckedAt: Timestamp | null;
  lastIncidentAt: Timestamp | null;
  archivedAt: Timestamp | null;
  order: number; // display order within its section
  /** Coverage begins at enablement; absent on components not yet migrated to v2. */
  monitoringStartedAt?: Timestamp;
  hourlyRollupThrough?: Timestamp;
  dailyRollupThrough?: Timestamp;
  hourlyHistoryGap?: { from: Timestamp; to: Timestamp; reason: "raw_retention_expired" };
  dailyHistoryGap?: { from: Timestamp; to: Timestamp; reason: "hourly_retention_expired" | "missing_completed_hours" };
}

export interface StatusCheck {
  timestamp: Timestamp;
  observedAt?: Timestamp;
  scheduledSlotAt?: Timestamp; // scheduled probes only; absent on legacy records
  ok: boolean;
  state: StatusState;
  latencyMs: number;
  errorCode: string | null;
  errorMsg: string | null;
  source: "scheduled" | "manual";
  expireAt: Timestamp; // v2 raw observations expire after 48 hours
}

export interface StatusRollup {
  periodStart: Timestamp;
  uptimePct: number | null; // unavailable when no known observations exist
  incidentCount: number;
  avgLatencyMs: number | null;
  sampleCount: number;
  /** v2 metadata is absent on legacy rollups, whose coverage remains unverified. */
  schemaVersion?: 2;
  complete?: boolean; // processing settled; does not imply full monitoring coverage
  coverageState?: "complete" | "partial" | "unavailable";
  knownSampleCount?: number;
  unknownSampleCount?: number;
  upSampleCount?: number;
  latencySampleCount?: number;
  totalLatencyMs?: number;
  scheduledSampleCount?: number;
  expectedSampleCount?: number; // may be below four in the first enabled hour
  missingSampleCount?: number;
  coveredHourCount?: number; // daily rollups only
  expectedHourCount?: number; // daily rollups only
  processedAt?: Timestamp;
  // v2: hourly retention is 7 days; daily retention is 90 days. Legacy daily docs may still have null.
  expireAt: Timestamp | null;
}

export interface StatusCooldown {
  lastPingedAt: Timestamp | null;
  lastPingedBy: string | null;
  nextAvailableAt: Timestamp | null;
}

// ─── Org Users (people who log into Studio Manager) ──────────────────────────

export type OrgUserRole = "PLATFORM_OWNER" | "PLATFORM_ADMIN" | "ORG_OWNER" | "ORG_ADMIN" | "ORG_REQUESTER";

export type PlatformRole = "PLATFORM_OWNER" | "PLATFORM_ADMIN";

export function isPlatformRole(role: OrgUserRole): role is PlatformRole {
  return role === "PLATFORM_OWNER" || role === "PLATFORM_ADMIN";
}

export interface OrgUser {
  email: string;
  name: string;
  photoURL: string | null;
  role: OrgUserRole;
  orgId: string | null; // null for PLATFORM_OWNER and PLATFORM_ADMIN
  status: "active" | "suspended";
  createdAt: Timestamp;
  lastLoginAt: Timestamp;
  mustChangePassword?: boolean; // WP4: set true at invite; cleared by ChangePasswordSection
  passwordChangedAt?: Timestamp | null;
  /** BiG-Support capabilities on platform users; written only by the supportStaff callable. */
  capabilities?: { partner?: boolean; billing?: boolean };
}

// ─── Groups ─────────────────────────────────────────────────────────────────

export type GroupStatus = "active" | "suspended";

export interface Group {
  studioId: string;
  orgId: string;
  name: string;
  contactEmail: string | null;
  contactName: string | null;
  defaultAccessGroupId: string | null; // Default access group for new users in this group
  notes: string | null;
  status: GroupStatus;
  createdAt: Timestamp;
  createdBy: string;
  updatedAt: Timestamp;
}

// ─── TOS ─────────────────────────────────────────────────────────────────────

export interface TosAcceptance {
  resourceId: string;
  resourceName: string;
  tosLabel: string;
  studioId: string;
  inviteLinkId: string;
  acceptedAt: Timestamp;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface TosConfig {
  required: boolean;
  label: string | null;
  resourceName: string | null;
  fileUrl: string | null;
}

// ─── End Users (people inside Pickaxe Studios) ───────────────────────────────

export type EndUserStatus = "invited" | "verified" | "active" | "suspended" | "inactive" | "archived" | "contact";

export interface EndUser {
  studioId: string;
  orgId: string;
  email: string;
  name: string;
  groupIds: string[]; // References to groups/{groupId}
  /** Mirrored down from contacts.tagIds by the onContactWritten trigger.
   *  Denormalized so automation conditions can match tags without a read. */
  tagIds?: string[];
  pickaxeAccessGroupId: string | null; // Pickaxe access group ID (format: access-<uuid>)
  pickaxeUserId: string | null; // Pickaxe internal user ID for history cross-referencing
  pickaxeTotalUses: number | null;
  pickaxeTotalMemories: number | null;
  pickaxeSignupDate: Timestamp | null;
  notes: string | null; // Maps to Pickaxe user.summary
  // @deprecated — tokens now at orgs/{orgId}/integrationTokens/{email}
  integrations?: {
    google: GoogleIntegration | null;
    microsoft: MicrosoftIntegration | null;
    primaryProvider: "google" | "microsoft" | null;
  };
  tosAcceptance?: TosAcceptance | null;
  status: EndUserStatus;
  userKind?: "workshop_contact" | "studio_user" | "contact"; // absent = studio_user (legacy docs)
  invitedAt: Timestamp;
  lastReinvitedAt: Timestamp | null;
  verifiedAt: Timestamp | null;
  lastActiveAt: Timestamp | null;
  createdAt: Timestamp;
  createdBy: string;
}

// ─── Invite Links ─────────────────────────────────────────────────────────

export type InviteLinkScope = "studio" | "access_group" | "group" | "individual";
export type InviteLinkStatus = "active" | "expired" | "exhausted" | "revoked";

export interface InviteLink {
  orgId: string;
  studioId: string;
  studioName: string;
  orgName: string;
  studioLoginUrl: string | null;

  // Scope determines what's pre-assigned when the link is used
  scope: InviteLinkScope;

  // Pre-assigned values based on scope
  accessGroupIds?: string[];          // Multi-group: array of Pickaxe access group IDs
  accessGroupNames?: string[];        // Multi-group: array of display names
  accessGroupId: string | null;       // Primary access group ID
  accessGroupName: string | null;     // Primary access group display name
  groupId: string | null;             // Firestore group doc ID — set for group/individual
  groupName: string | null;           // Display name, denormalized
  targetEmail: string | null;         // Set only for individual scope
  targetName: string | null;          // Set only for individual scope

  // Link metadata
  maxUses: number;                    // 0 = unlimited
  currentUses: number;
  expiresAt: Timestamp | null;        // null = never expires
  status: InviteLinkStatus;

  // Tracking
  createdAt: Timestamp;
  createdBy: string;
  createdByEmail: string;
  lastUsedAt: Timestamp | null;

  // Legacy fields (backward compat with old invite links)
  tierIds?: string[];
  tierNames?: string[];
  tierId?: string | null;
  tierName?: string | null;
  productIds?: string[];
  productNames?: string[];
  groupIds?: string[];
  useCount?: number;
  isActive?: boolean;
}

// ─── Fee Schedules ───────────────────────────────────────────────────────────

export interface FeeSchedule {
  orgId: string;
  studioId: string;
  pickaxeAccessGroupId: string;
  accessGroupName: string;
  name: string;
  types: {
    flat: {
      enabled: boolean;
      amount: number;
    };
    perUser: {
      enabled: boolean;
      amount: number;
      cadence: "daily" | "monthly" | "quarterly" | "annual";
    };
    usage: {
      enabled: boolean;
      baseAmount: number;
      upchargePerUnit: number;
      unit: "conversation";
      perUserGranularity: boolean;
    };
  };
  effectiveFrom: Timestamp;
  effectiveTo: Timestamp | null;
  createdAt: Timestamp;
  createdBy: string;
}

// ─── Usage Events ────────────────────────────────────────────────────────────

export type UsageEventType = "conversation_ended" | "signup" | "payment";

export interface UsageEvent {
  orgId: string;
  studioId: string;
  endUserId: string | null;
  userEmail: string;
  eventType: UsageEventType;
  occurredAt: Timestamp;
  metadata: Record<string, unknown>;
}

// ─── Billing Reports ─────────────────────────────────────────────────────────

export interface BillingLineItem {
  label: string;
  type: "flat" | "per_user" | "usage";
  quantity: number;
  unitAmount: number;
  subtotal: number;
  detail: Record<string, unknown>;
}

export interface BillingReport {
  orgId: string;
  studioId: string;
  periodStart: Timestamp;
  periodEnd: Timestamp;
  status: "draft" | "sent";
  lineItems: BillingLineItem[];
  totalAmount: number;
  feeScheduleId: string;
  pdfUrl: string | null;
  sentAt: Timestamp | null;
  sentTo: string[];
  createdAt: Timestamp;
  createdBy: string;
}

// ─── Resources ──────────────────────────────────────────────────────────────

export type ResourceScope = "platform" | "org" | "platform_internal";

export interface Resource {
  scope: ResourceScope;
  orgId: string | null; // null for platform resources
  name: string;
  description: string | null;
  fileName: string; // original file name
  fileUrl: string; // Firebase Storage download URL
  storagePath: string; // full Storage path for deletion
  fileType: string; // MIME type
  fileSize: number; // bytes
  uploadedBy: string;
  uploadedByName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// ─── Audit Log ───────────────────────────────────────────────────────────────

export interface AuditLogEntry {
  actorUid: string;
  actorEmail: string;
  orgId: string | null;
  action: string;
  targetType: string;
  targetId: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: Timestamp;
}
